import * as React from "react";

import { useNotification } from "@/shared/components/notifications/useNotification";

import { useWorkspaceTimer } from "../../../WorkspaceCtx/hooks";
import { pixelToDomainX } from "../chartXDomainMapping";
import { PlotLoadingState } from "../messaging/events";
import { Extents } from "../PlotRenderer";
import {
  resolveAlignedSeekTarget,
  SeekAlignment,
} from "../resolveAlignedSeekTarget";

/**
 * Hold the timer while the plot is loading.
 */
export function useHoldWhileLoading(isPlotLoading: PlotLoadingState) {
  const timer = useWorkspaceTimer();
  const timerHoldRef = React.useRef<symbol | null>(null);
  React.useEffect(
    function holdTimerWhenLoading() {
      const loadingForeground = isPlotLoading === "foreground";
      if (loadingForeground && timerHoldRef.current === null) {
        timerHoldRef.current = timer.hold();
      }
      if (!loadingForeground && timerHoldRef.current !== null) {
        timer.holdRelease(timerHoldRef.current);
        timerHoldRef.current = null;
      }

      return function cleanUp() {
        if (timerHoldRef.current !== null) {
          timer.holdRelease(timerHoldRef.current);
          timerHoldRef.current = null;
        }
      };
    },
    [isPlotLoading, timer],
  );
}

/**
 * The notice shown after a click that produced no seek. The wording differs by
 * panel mode because the failures do: an aligned click can miss every session
 * containing the playhead, while an absolute-time click can land in a gap
 * between the workspace's time spans.
 */
export function failedSeekExplanation({
  aligned,
}: {
  aligned: boolean;
}): string {
  return aligned
    ? "No session can play from the clicked position"
    : "The clicked time falls in a gap with no data";
}

/**
 * How long repeat failed-seek notices are suppressed after one is shown. It
 * matches a notification's default auto-dismiss delay, so clicking repeatedly
 * never puts two copies of the same notice on screen.
 */
const FAILED_SEEK_NOTICE_SUPPRESSION_MS = 3000;

/**
 * Seek the timer to the time the user clicks on the plot.
 *
 * On an absolute-time panel the click names a timestamp directly. On an
 * aligned panel it names an elapsed x position, which is resolved against the
 * sessions currently containing the playhead (see
 * {@link resolveAlignedSeekTarget}).
 *
 * A click that resolves to no time shows a brief notice (see
 * {@link failedSeekExplanation}) instead of doing nothing. On an aligned panel
 * that happens often: the x domain spans the longest session, so a click past
 * a shorter session's end has nothing to seek to.
 */
export function useSeekToClick(
  chartExtents: Extents | null,
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  isDragging: boolean,
  seekAlignment: SeekAlignment | null,
) {
  const timer = useWorkspaceTimer();
  const showNotification = useNotification();
  // The click handler reads all three of these only once a click arrives, so
  // they are mirrored into refs rather than listed as effect dependencies. The
  // panel rebuilds seekAlignment on every render, so depending on it would
  // tear down and re-register the click listener just as often.
  const showNotificationRef = React.useRef(showNotification);
  showNotificationRef.current = showNotification;
  const isDraggingRef = React.useRef(isDragging);
  isDraggingRef.current = isDragging;
  const seekAlignmentRef = React.useRef(seekAlignment);
  seekAlignmentRef.current = seekAlignment;
  const lastFailedSeekNoticeAtRef = React.useRef(0);
  React.useEffect(
    function attachSeekToListener() {
      if (chartExtents === null || canvasRef.current === null) {
        return;
      }
      const canvas = canvasRef.current;
      if (canvas === null) {
        return;
      }

      const abortController = new AbortController();
      function notifyFailedSeek(aligned: boolean) {
        const explanation = failedSeekExplanation({ aligned });
        const now = Date.now();
        if (
          now - lastFailedSeekNoticeAtRef.current <
          FAILED_SEEK_NOTICE_SUPPRESSION_MS
        ) {
          return;
        }
        lastFailedSeekNoticeAtRef.current = now;
        showNotificationRef.current(explanation, "info");
      }
      function seekToTime(event: MouseEvent) {
        if (chartExtents === null || canvas === null) {
          return;
        }

        // The click that ends a pan lands here too; usePan keeps isDragging
        // set until after this listener has run so that click cannot seek.
        if (isDraggingRef.current) {
          return;
        }

        const {
          pixel: { left: canvasScaleLeftOffset, width },
          data: {
            viewportLogTime: { min, max },
            viewportDomain,
          },
        } = chartExtents;

        // A chart area the panel has not laid out yet has no width, and no
        // time lies under any of its pixels: the x / width below would be NaN,
        // which BigInt() rejects by throwing.
        if (width <= 0) {
          return;
        }

        const canvasBox = canvas.getBoundingClientRect();

        const x = Math.max(
          Math.min(
            event.clientX - canvasScaleLeftOffset - canvasBox.left,
            width,
          ),
          0,
        );

        const currentSeekAlignment = seekAlignmentRef.current;
        if (currentSeekAlignment !== null) {
          // x is measured from the chart area's left edge, so the pixel
          // origin here is 0 rather than the chart's offset on the canvas.
          const clickedSeconds = pixelToDomainX(x, {
            viewportDomain,
            pixel: { left: 0, width },
          });
          if (clickedSeconds === undefined) {
            // pixelToDomainX fails only on a zero-width x domain, which
            // says nothing about the click worth telling the user.
            return;
          }
          const target = resolveAlignedSeekTarget({
            series: currentSeekAlignment.series,
            alignment: currentSeekAlignment.alignment,
            currentTimeNs: timer.currentTime,
            clickedSeconds,
          });
          const dispatched = target !== undefined && timer.seekTo(target);
          if (!dispatched) {
            notifyFailedSeek(true);
          }
          return;
        }

        const nextTime =
          min + BigInt(Math.round(Number(max - min) * (x / width)));

        if (!timer.seekTo(nextTime)) {
          notifyFailedSeek(false);
        }
      }
      canvas.addEventListener("click", seekToTime, {
        signal: abortController.signal,
      });

      return function detachSeekToListener() {
        abortController.abort();
      };
    },
    [chartExtents, canvasRef, timer],
  );
}
